/**
 * Move focus to the first candidate that is still in the document and will
 * actually take it.
 *
 * **Written once because it was written three times.** `Overlay` restores through a
 * three-step fallback — the element that opened it, then a caller-named ref,
 * then the content region (`contentRegion()`, #605); `ToastProvider`
 * moves focus off a control it is about to unmount, twice, with two different
 * fallback depths. web-architect measured the divergence: one chain had no last
 * rung at all, so a keyboard user landed on `<body>` — the failure this repo has
 * already fixed twice and the one `789f0ce`'s message claims to close. Three
 * implementations of one idea, with three behaviours, is the duplication the
 * brief records as the three-progress-rings failure, and it arrived one commit
 * after Task 1 was told to reuse rather than rebuild.
 *
 * **`document.contains` is not enough, which is the half every copy missed.**
 * `.focus()` on a detached node does nothing and throws nothing — that is why
 * the containment check exists — but a node can be attached and still refuse
 * focus: `display: none`, `disabled`, or inside an `inert` subtree. The last is
 * not hypothetical here, since `Overlay` inerts the whole app while it is open.
 * So the move is CONFIRMED rather than assumed, and an element that declines it
 * falls through to the next candidate instead of silently ending the chain.
 *
 * Returns whether focus landed anywhere, so a caller that has somewhere else to
 * go can tell the difference between "restored" and "gave up".
 */
export function focusFirstAlive(...candidates: (Element | null | undefined)[]): boolean {
  for (const candidate of candidates) {
    if (!(candidate instanceof HTMLElement) || !document.contains(candidate)) continue
    // `<body>` is never a landing place — it IS the failure this module exists
    // to prevent, and without this line it silently succeeds as a candidate.
    //
    // `document.activeElement` is `<body>` whenever nothing holds focus, so a
    // caller that captures "whatever was focused before" captures `<body>` and
    // passes it in as the FIRST rung. `body.focus()` then makes
    // `document.activeElement === body` true, the confirmation check above is
    // satisfied, and the chain returns before it ever reaches the caller's
    // named fallback — so the fallback is unreachable in exactly the case it
    // was written for. Measured in Chrome: opening the notifications drawer by
    // keyboard shortcut with nothing focused, then pressing Escape, left focus
    // on `<body>` 3 runs out of 3, while the four paths that had a real
    // activeElement all restored correctly.
    if (candidate === document.body) continue
    candidate.focus()
    if (document.activeElement === candidate) return true
  }
  return false
}

/**
 * The app's content region — the last rung when nothing better survives.
 *
 * `<main tabindex="-1">` is the skip link's target under the chrome, so landing
 * there is the destination the app already offers a keyboard user for "take me
 * to the content". It is also the pattern `Security.tsx` reaches for after a
 * mutation: the top of the region that changed, rather than the top of the
 * document. `ChromeLayout` and `BareLayout` both carry it — the second gained
 * it for this, since the auth screens are where `ToastProvider` most needs a
 * last rung and they had none.
 *
 * **The attribute is part of the selector deliberately**, and that stays true
 * now that both layouts satisfy it: focusing a `<main>` WITHOUT it is a no-op
 * that reads as a successful restore, which is the silent-failure shape this
 * whole module exists for. A future layout that forgets the attribute should
 * fall through to nothing rather than appear to have been handled.
 */
export const contentRegion = (): HTMLElement | null =>
  document.querySelector<HTMLElement>('main[tabindex]')
